/*
 * src/components/SessionWarningDialog/SessionWarningDialog.tsx
 * 
 * User-friendly session warning dialog that appears before auto-logout
 */

import React from 'react';
import {
  Typography,
  LinearProgress,
} from '@mui/material';
import { Warning as WarningIcon, AccessTime as TimeIcon } from '@mui/icons-material';
import Modal from '../Modal/Modal';
import * as styles from './SessionWarningDialog.module.scss';
import invidooLogo from '../../../static/invidoo-logo.svg';
import { getSessionConfig } from '../../config/session';

interface SessionWarningDialogProps {
  open: boolean;
  timeRemaining: number;
  type: 'idle' | 'absolute' | 'token';
  onExtendSession: () => void;
  onLogoutNow: () => void;
}



const getProgressValue = (timeRemaining: number, totalTime: number): number => {
  return Math.max(0, Math.min(100, (timeRemaining / totalTime) * 100));
};

const SessionWarningDialog: React.FC<SessionWarningDialogProps> = ({
  open,
  timeRemaining,
  type,
  onExtendSession,
  onLogoutNow,
}) => {
  const sessionConfig = getSessionConfig();
  const totalWarningTime = sessionConfig.WARNING_TIME; // Use configured warning time
  const progressValue = getProgressValue(timeRemaining, totalWarningTime);
  const isUrgent = timeRemaining < 60000; // Less than 1 minute remaining
  
  const getDialogConfig = () => {
    switch (type) {
      case 'idle':
        return {
          title: 'Du bliver snart logget ud',
          icon: <TimeIcon className={styles.warningIcon} />,
          colorClass: 'warning',
          allowExtend: true,
          description: 'Vi har bemærket, at du ikke har været aktiv i et stykke tid. For at beskytte dine data logger vi dig automatisk ud.',
        };
      case 'absolute':
        return {
          title: 'Tid til at logge ind igen',
          icon: <WarningIcon className={styles.errorIcon} />,
          colorClass: 'error',
          allowExtend: false,
          description: 'Du har været logget ind i lang tid. Af sikkerhedsmæssige årsager skal du logge ind igen.',
        };
      case 'token':
        return {
          title: 'Din login udløber snart',
          icon: <WarningIcon className={styles.warningIcon} />,
          colorClass: 'warning',
          allowExtend: true,
          description: 'Din login er ved at udløbe. Klik på "Forbliv logget ind" for at fortsætte.',
        };
      default:
        return {
          title: 'Sikkerhedsbesked',
          icon: <WarningIcon className={styles.warningIcon} />,
          colorClass: 'warning',
          allowExtend: true,
          description: 'Der opstod et sikkerhedsproblem.',
        };
    }
  };

  const config = getDialogConfig();

  const modalContent = (
    <div className={styles.sessionWarningContent}>
      <div className={styles.sessionWarningLogo}>
        <img src={invidooLogo} alt="invidoo logo" className={styles.logo} />
      </div>
      {/* Header with Icon and Title */}
      <div className={styles.warningHeader}>
        <div className={styles.iconContainer}>
          {config.icon}
        </div>
        <Typography variant="h5" component="h2" className="panel-title">
          {config.title}
        </Typography>
      </div>

      {/* Description */}
      {config.description && (
        <Typography variant="body1" className={styles.description}>
          {config.description}
        </Typography>
      )}

      {/* Progress Bar */}
      <div className={styles.progressContainer}>
        <LinearProgress 
          variant="determinate" 
          value={progressValue}
          className={`${styles.progressBar} ${styles[config.colorClass]} ${isUrgent ? styles.urgent : ''}`}
        />
      </div>

      {/* Idle Hint */}
      {type === 'idle' && (
        <Typography variant="body2" className={styles.idleHint}>
          💡 Klik på "Forbliv logget ind" hvis du vil fortsætte uden at logge ud
        </Typography>
      )}
    </div>
  );


  // Debug handlers
  const handleExtendClick = () => {
    if (onExtendSession) {
      onExtendSession();
    }
  };

  const handleLogoutClick = () => {
    if (onLogoutNow) {
      onLogoutNow();
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleLogoutClick} // "Logout Now" is the cancel action
      onConfirm={config.allowExtend ? handleExtendClick : handleLogoutClick} // "Stay Logged In" or just "Logout Now"
      title="" // We handle title inside content
      confirmText={config.allowExtend ? "Forbliv logget ind" : "Ok"}
      cancelText={config.allowExtend ? "Log ud nu" : undefined}
      showConfirmButton={true}
      showCancelButton={config.allowExtend}
      className={`${styles.sessionWarningDialog} ${styles[`type-${type}`]}`}
      centered={true}
      width="50rem"
      hasBorderBox={false}
    >
      {modalContent}
    </Modal>
  );
};

export default SessionWarningDialog; 